/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { useTheme } from "./ThemeContext"; // Fixed import path

export type ProfileType = "learner" | "parent";

export interface LearnerProfile {
  id: string;
  name: string;
  emoji: string;
  color: string; // gradient classes or style string
  type: ProfileType;
  className: string; // e.g. "Class 10"
  board: string; // CBSE / ICSE / State
  age: number;
  goals: string[]; // learning goals
  // per-profile dynamic state
  streak: number;
  xp: number;
  badgesEarned: number;
  videosWatched: number;
  quizAccuracy: number;
  notesCreated: number;
  currentChapter: string;
  currentProgress: number;
  videosLeft: number;
  enrolledSubjects: string[]; // subject names
}

interface ProfileContextValue {
  profiles: LearnerProfile[];
  activeProfileId: string;
  activeProfile: LearnerProfile;
  setActiveProfileId: (id: string) => void;
  addProfile: (p: Omit<LearnerProfile, "id">) => LearnerProfile;
  updateProfile: (id: string, patch: Partial<LearnerProfile>) => void;
  deleteProfile: (id: string) => void;
}

const STORAGE_KEY = "ekluvya_profiles_v1";
const ACTIVE_KEY = "ekluvya_active_profile_v1";

// Updated avatar options with more educational and fun emojis
export const AVATAR_OPTIONS = [
  "🎓",
  "📚",
  "🧠",
  "⚡",
  "🚀",
  "🌟",
  "💡",
  "🎯",
  "🦁",
  "🦊",
  "🐼",
  "🐯",
  "🐵",
  "🐨",
  "🦄",
  "🦅",
];

// Updated color options with theme-compatible gradient styles
export const COLOR_OPTIONS = [
  "gradient-brand",
  "gradient-blue",
  "gradient-green",
  "gradient-amber",
  "gradient-purple",
  "gradient-pink",
];

// Helper to get actual gradient CSS
export const getGradientStyle = (
  colorClass: string,
  theme: "light" | "dark" = "light",
) => {
  const gradients: Record<string, string> = {
    "gradient-brand": `linear-gradient(135deg, var(--color-orange), var(--color-orange-alt))`,
    "gradient-blue": `linear-gradient(135deg, #3b82f6, #06b6d4)`,
    "gradient-green": `linear-gradient(135deg, #10b981, #14b8a6)`,
    "gradient-amber": `linear-gradient(135deg, #f59e0b, #f97316)`,
    "gradient-purple": `linear-gradient(135deg, #8b5cf6, #d946ef)`,
    "gradient-pink": `linear-gradient(135deg, #ec4899, #f43f5e)`,
  };

  // For dark mode, slightly adjust colors
  if (theme === "dark") {
    gradients["gradient-brand"] =
      `linear-gradient(135deg, var(--color-orange), var(--color-orange-alt))`;
    gradients["gradient-blue"] = `linear-gradient(135deg, #2563eb, #0891b2)`;
    gradients["gradient-green"] = `linear-gradient(135deg, #059669, #0d9488)`;
    gradients["gradient-amber"] = `linear-gradient(135deg, #d97706, #ea580c)`;
    gradients["gradient-purple"] = `linear-gradient(135deg, #7c3aed, #c026d3)`;
    gradients["gradient-pink"] = `linear-gradient(135deg, #db2777, #e11d48)`;
  }

  return gradients[colorClass] || gradients["gradient-brand"];
};

export const CLASS_OPTIONS = [
  "Class 6",
  "Class 7",
  "Class 8",
  "Class 9",
  "Class 10",
  "Class 11",
  "Class 12",
];

export const BOARD_OPTIONS = ["CBSE", "ICSE", "State Board", "IB", "IGCSE"];

export const GOAL_OPTIONS = [
  "Score 95%+ in board exams",
  "Crack JEE / NEET",
  "Master concepts, not memorize",
  "Improve weak subjects",
  "Daily revision habit",
  "Complete syllabus early",
  "Build strong fundamentals",
  "Excel in competitive exams",
];

const defaultProfiles: LearnerProfile[] = [
  {
    id: "p_arjun",
    name: "Arjun",
    emoji: "🦁",
    color: "gradient-brand",
    type: "learner",
    className: "Class 10",
    board: "CBSE",
    age: 15,
    goals: ["Score 95%+ in board exams", "Master concepts, not memorize"],
    streak: 7,
    xp: 2840,
    badgesEarned: 3,
    videosWatched: 42,
    quizAccuracy: 78,
    notesCreated: 12,
    currentChapter: "Quadratic Equations",
    currentProgress: 74,
    videosLeft: 3,
    enrolledSubjects: ["Mathematics", "Science", "English", "Social Science"],
  },
  {
    id: "p_priya",
    name: "Priya",
    emoji: "🦊",
    color: "gradient-blue",
    type: "learner",
    className: "Class 8",
    board: "CBSE",
    age: 13,
    goals: ["Daily revision habit", "Improve weak subjects"],
    streak: 3,
    xp: 1240,
    badgesEarned: 1,
    videosWatched: 18,
    quizAccuracy: 71,
    notesCreated: 5,
    currentChapter: "Linear Equations",
    currentProgress: 38,
    videosLeft: 5,
    enrolledSubjects: ["Mathematics", "Science", "English"],
  },
];

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profiles, setProfiles] = useState<LearnerProfile[]>(() => {
    if (typeof window === "undefined") return defaultProfiles;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as LearnerProfile[];
        // Migrate old color format if needed
        return parsed.map((p) => ({
          ...p,
          color: p.color?.startsWith("from-")
            ? p.color.replace("from-brand to-brand-light", "gradient-brand")
            : p.color,
        }));
      }
    } catch {}
    return defaultProfiles;
  });

  const [activeProfileId, setActiveProfileIdState] = useState<string>(() => {
    if (typeof window === "undefined") return defaultProfiles[0].id;
    return localStorage.getItem(ACTIVE_KEY) || defaultProfiles[0].id;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
  }, [profiles]);

  useEffect(() => {
    localStorage.setItem(ACTIVE_KEY, activeProfileId);
  }, [activeProfileId]);

  const setActiveProfileId = (id: string) => {
    if (profiles.some((p) => p.id === id)) setActiveProfileIdState(id);
  };

  const addProfile: ProfileContextValue["addProfile"] = (p) => {
    const newProfile: LearnerProfile = {
      ...p,
      id: `p_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      streak: 0,
      xp: 0,
      badgesEarned: 0,
      videosWatched: 0,
      quizAccuracy: 0,
      notesCreated: 0,
      currentChapter: "Get Started",
      currentProgress: 0,
      videosLeft: 0,
      enrolledSubjects: p.enrolledSubjects || [
        "Mathematics",
        "Science",
        "English",
      ],
    };
    setProfiles((prev) => [...prev, newProfile]);
    return newProfile;
  };

  const updateProfile: ProfileContextValue["updateProfile"] = (id, patch) => {
    setProfiles((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );
  };

  const deleteProfile: ProfileContextValue["deleteProfile"] = (id) => {
    setProfiles((prev) => {
      const next = prev.filter((p) => p.id !== id);
      if (next.length === 0) return prev; // keep at least one
      if (activeProfileId === id) setActiveProfileIdState(next[0].id);
      return next;
    });
  };

  const activeProfile =
    profiles.find((p) => p.id === activeProfileId) || profiles[0];

  const value = useMemo(
    () => ({
      profiles,
      activeProfileId: activeProfile.id,
      activeProfile,
      setActiveProfileId,
      addProfile,
      updateProfile,
      deleteProfile,
    }),
    [profiles, activeProfile],
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
}

// Helper hook to get profile colors with theme support
export function useProfileColors() {
  const { theme } = useTheme();
  const { activeProfile } = useProfile();

  const gradientStyle = getGradientStyle(activeProfile.color, theme);

  return {
    gradientStyle,
    colorClass: activeProfile.color,
  };
}

// Helper component for profile avatar
interface ProfileAvatarProps {
  profile?: LearnerProfile;
  size?: number;
  className?: string;
  onClick?: () => void;
}

export function ProfileAvatar({
  profile,
  size = 40,
  className = "",
  onClick,
}: ProfileAvatarProps) {
  const { theme } = useTheme();
  const { activeProfile } = useProfile();
  const targetProfile = profile || activeProfile;

  const gradientStyle = getGradientStyle(targetProfile.color, theme);

  // Responsive size adjustment
  const finalSize = typeof size === "number" ? size : 40;
  const responsiveSize = `clamp(${Math.max(32, finalSize * 0.8)}px, ${finalSize}px, ${Math.min(80, finalSize * 1.2)}px)`;

  return (
    <div
      onClick={onClick}
      style={{
        width: responsiveSize,
        height: responsiveSize,
        borderRadius: "50%",
        background: gradientStyle,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: `calc(${responsiveSize} * 0.5)`,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s ease",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        flexShrink: 0,
      }}
      className={className}
    >
      {targetProfile.emoji}
    </div>
  );
}

// Helper component for profile card
interface ProfileCardProps {
  profile: LearnerProfile;
  isActive?: boolean;
  onClick?: () => void;
  compact?: boolean;
}

export function ProfileCard({
  profile,
  isActive,
  onClick,
  compact = false,
}: ProfileCardProps) {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const gradientStyle = getGradientStyle(profile.color, theme);

  const cardStyle: React.CSSProperties = {
    background: isDark ? "rgba(31, 41, 55, 0.95)" : "#ffffff",
    border: `1px solid ${isActive ? "var(--color-orange)" : isDark ? "#374151" : "var(--color-border)"}`,
    borderRadius: "var(--radius-lg)",
    padding: compact ? "clamp(10px, 3vw, 12px)" : "clamp(14px, 4vw, 20px)",
    cursor: onClick ? "pointer" : "default",
    transition: "all 0.2s ease",
    boxShadow: isActive
      ? "0 0 0 2px var(--color-orange), 0 4px 12px rgba(0,0,0,0.1)"
      : "var(--shadow-sm)",
    width: "100%",
  };

  if (compact) {
    return (
      <div style={cardStyle} onClick={onClick}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(10px, 3vw, 12px)",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: "clamp(32px, 8vw, 40px)",
              height: "clamp(32px, 8vw, 40px)",
              borderRadius: "50%",
              background: gradientStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "clamp(16px, 4vw, 20px)",
              flexShrink: 0,
            }}
          >
            {profile.emoji}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: "clamp(13px, 3vw, 14px)",
                color: isDark ? "#f9fafb" : "var(--color-text-dark)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {profile.name}
            </div>
            <div
              style={{
                fontSize: "clamp(10px, 2.5vw, 11px)",
                color: isDark ? "#9ca3af" : "var(--color-text-mid)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {profile.className} · {profile.xp.toLocaleString()} XP
            </div>
          </div>
          {isActive && (
            <div
              style={{
                width: "clamp(6px, 1.5vw, 8px)",
                height: "clamp(6px, 1.5vw, 8px)",
                borderRadius: "50%",
                backgroundColor: "var(--color-orange)",
                flexShrink: 0,
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle} onClick={onClick}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "clamp(12px, 3vw, 16px)",
          marginBottom: "clamp(10px, 2.5vw, 12px)",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            width: "clamp(48px, 10vw, 60px)",
            height: "clamp(48px, 10vw, 60px)",
            borderRadius: "50%",
            background: gradientStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "clamp(24px, 5vw, 30px)",
            flexShrink: 0,
          }}
        >
          {profile.emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "clamp(16px, 3.5vw, 18px)",
              fontWeight: "bold",
              color: isDark ? "#f9fafb" : "var(--color-text-dark)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {profile.name}
          </div>
          <div
            style={{
              fontSize: "clamp(11px, 2.5vw, 12px)",
              color: isDark ? "#9ca3af" : "var(--color-text-mid)",
              marginTop: "4px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {profile.className} · {profile.board}
          </div>
        </div>
        {isActive && (
          <div
            style={{
              padding: "clamp(2px, 1vw, 4px) clamp(6px, 2vw, 8px)",
              borderRadius: "9999px",
              background: "var(--color-orange)",
              color: "#fff",
              fontSize: "clamp(9px, 2vw, 10px)",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Active
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: "clamp(10px, 2.5vw, 12px)",
          display: "flex",
          gap: "clamp(12px, 3vw, 16px)",
          flexWrap: "wrap",
        }}
      >
        <div style={{ textAlign: "center", flex: 1, minWidth: "60px" }}>
          <div
            style={{
              fontSize: "clamp(18px, 4vw, 20px)",
              fontWeight: "bold",
              color: isDark ? "#f9fafb" : "var(--color-text-dark)",
            }}
          >
            {profile.streak}
          </div>
          <div
            style={{
              fontSize: "clamp(9px, 2vw, 10px)",
              color: isDark ? "#9ca3af" : "var(--color-text-mid)",
            }}
          >
            Day Streak
          </div>
        </div>
        <div style={{ textAlign: "center", flex: 1, minWidth: "60px" }}>
          <div
            style={{
              fontSize: "clamp(18px, 4vw, 20px)",
              fontWeight: "bold",
              color: isDark ? "#f9fafb" : "var(--color-text-dark)",
            }}
          >
            {profile.xp.toLocaleString()}
          </div>
          <div
            style={{
              fontSize: "clamp(9px, 2vw, 10px)",
              color: isDark ? "#9ca3af" : "var(--color-text-mid)",
            }}
          >
            Total XP
          </div>
        </div>
        <div style={{ textAlign: "center", flex: 1, minWidth: "60px" }}>
          <div
            style={{
              fontSize: "clamp(18px, 4vw, 20px)",
              fontWeight: "bold",
              color: isDark ? "#f9fafb" : "var(--color-text-dark)",
            }}
          >
            {profile.badgesEarned}
          </div>
          <div
            style={{
              fontSize: "clamp(9px, 2vw, 10px)",
              color: isDark ? "#9ca3af" : "var(--color-text-mid)",
            }}
          >
            Badges
          </div>
        </div>
      </div>

      {profile.goals.length > 0 && (
        <div
          style={{
            marginTop: "clamp(10px, 2.5vw, 12px)",
            paddingTop: "clamp(10px, 2.5vw, 12px)",
            borderTop: `1px solid ${isDark ? "#374151" : "var(--color-border)"}`,
          }}
        >
          <div
            style={{
              fontSize: "clamp(10px, 2.5vw, 11px)",
              fontWeight: 600,
              color: isDark ? "#9ca3af" : "var(--color-text-mid)",
              marginBottom: "clamp(4px, 1.5vw, 6px)",
            }}
          >
            Learning Goals
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "clamp(4px, 1.5vw, 6px)",
            }}
          >
            {profile.goals.slice(0, 2).map((goal, idx) => (
              <span
                key={idx}
                style={{
                  fontSize: "clamp(9px, 2vw, 10px)",
                  padding: "2px 8px",
                  borderRadius: "9999px",
                  background: isDark
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                  color: isDark ? "#9ca3af" : "var(--color-text-mid)",
                  whiteSpace: "nowrap",
                }}
              >
                {goal.length > 30 ? goal.substring(0, 27) + "..." : goal}
              </span>
            ))}
            {profile.goals.length > 2 && (
              <span
                style={{
                  fontSize: "clamp(9px, 2vw, 10px)",
                  color: "var(--color-orange)",
                  whiteSpace: "nowrap",
                }}
              >
                +{profile.goals.length - 2} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
